// The live-link status — parallel to, and independent of, the workbook's own
// save state (giraffe/persist.ts's SaveState): that one asks "is this
// workbook saved into the Giraffe project", this one asks "is this workbook
// saved into the linked .xlsx/.json pair on disk". A workbook can be fully
// saved to the project and still show "Linked, unsaved" here, or vice versa.

import {
  FOLDER_CHOSEN_MESSAGE,
  openLinkPopup,
  requestPopupWrite,
  type FolderChosenMessage,
} from './linkPopup';
import { checkBrowserSupport, queryFolderPermission, restoreLinkTarget, clearLinkTarget } from './linkFsAccess';

export type LinkStatusKind = 'unlinked' | 'linked-unsaved' | 'linked-saved';

export interface LinkStatus {
  kind: LinkStatusKind;
  /** The linked `.xlsx`'s own name, once known. */
  fileName: string | null;
  detail: string;
}

type Listener = () => void;

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export class LinkController {
  private popup: Window | null = null;
  private status: LinkStatus = { kind: 'unlinked', fileName: null, detail: 'Not linked to a file.' };
  private listeners = new Set<Listener>();
  /** The bytes gTables itself last wrote for the linked .xlsx — the on-save diff's baseline. */
  private lastExportedBytes: ArrayBuffer | null = null;

  constructor() {
    window.addEventListener('message', this.onMessage);
    void this.restore();
  }

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getStatus(): LinkStatus {
    return this.status;
  }

  private setStatus(next: LinkStatus): void {
    this.status = next;
    for (const l of this.listeners) l();
  }

  private onMessage = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type !== FOLDER_CHOSEN_MESSAGE) return;
    const msg = event.data as FolderChosenMessage;
    this.setStatus({ kind: 'linked-unsaved', fileName: msg.workbookFileName, detail: `Linked to ${msg.workbookFileName} — not yet saved.` });
  };

  /** On load: a target remembered from a previous session, if its folder permission still holds. */
  private async restore(): Promise<void> {
    const target = await restoreLinkTarget();
    if (!target) return;
    const permission = await queryFolderPermission(target.folderHandle);
    this.setStatus(
      permission === 'granted'
        ? { kind: 'linked-saved', fileName: target.workbookFileName, detail: `Linked to ${target.workbookFileName}.` }
        : { kind: 'unlinked', fileName: target.workbookFileName, detail: `Click to re-confirm access to ${target.workbookFileName}.` }
    );
  }

  /** The user clicked the status indicator. */
  open(): void {
    const support = checkBrowserSupport();
    if (!support.supported) {
      this.setStatus({ ...this.status, detail: support.reason ?? 'Not supported in this browser.' });
      return;
    }
    const popup = openLinkPopup();
    this.popup = popup;
    if (!popup) {
      this.setStatus({ ...this.status, detail: 'The browser blocked the link window — allow popups for this site, then click again.' });
    }
    // The popup drives picking/re-confirming on its own; a FOLDER_CHOSEN
    // message arrives via `onMessage` above whenever that succeeds.
  }

  /**
   * Write the linked JSON, then read the `.xlsx` back and compare it against
   * what gTables itself last exported — surfacing it rather than silently
   * overwriting if the file changed independently (someone edited it in
   * Excel). `currentExportBytes` is passed only right after a fresh
   * "Export workbook as linked .xlsx", to refresh the baseline; omit it on an
   * ordinary autosave.
   */
  async save(jsonFileName: string, json: string, currentExportBytes?: ArrayBuffer): Promise<void> {
    if (this.status.kind === 'unlinked' || !this.status.fileName) return;
    const fileName = this.status.fileName;
    if (currentExportBytes) this.lastExportedBytes = currentExportBytes;

    try {
      const written = await requestPopupWrite(this.popup, { kind: 'writeJson', fileName: jsonFileName, content: json });
      if (!written.ok) throw new Error(written.error ?? 'The write was refused.');

      if (!this.lastExportedBytes) {
        this.setStatus({ kind: 'linked-saved', fileName, detail: `Saved ${jsonFileName}.` });
        return;
      }
      const read = await requestPopupWrite(this.popup, { kind: 'readXlsx' });
      if (read.ok && read.bytes && !bytesEqual(new Uint8Array(read.bytes), new Uint8Array(this.lastExportedBytes))) {
        this.setStatus({
          kind: 'linked-saved',
          fileName,
          detail: `Saved ${jsonFileName}. ${fileName} has changed independently since it was exported — re-download it from File ▸ Export workbook as linked .xlsx if gTables' version should win, or re-import it (File ▸ Import a workbook…, which only touches ordinary sheets) if Excel's should.`,
        });
        return;
      }
      this.setStatus({ kind: 'linked-saved', fileName, detail: `Saved ${jsonFileName}.` });
    } catch (err) {
      this.setStatus({ kind: 'linked-unsaved', fileName, detail: err instanceof Error ? err.message : 'Save failed.' });
    }
  }

  async unlink(): Promise<void> {
    await clearLinkTarget();
    this.popup = null;
    this.lastExportedBytes = null;
    this.setStatus({ kind: 'unlinked', fileName: null, detail: 'Not linked to a file.' });
  }
}

export const linkController = new LinkController();
